import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { analyticsFactory } from '@app/analytics/handlers';
import ChapterBookmark from '@app/components/chapters/bookmark';
import ChapterBookmarkFavorite from '@app/components/chapters/bookmark.favorite';
import { calculateFontSize, calculateLineHeight, useChapterSettings } from '@app/components/chapters/hooks';
import ContentComponent from '@app/components/content';
import { useTheme } from '@app/components/hooks';
import { LoadingFallback } from '@app/components/loading';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import ChapterCallToAction from '@app/domains/chapter/components/ChapterCallToAction';
import ChapterEditButton from '@app/domains/chapter/components/ChapterEditButton';
import ChapterLock from '@app/domains/chapter/components/ChapterLock';
import ChapterParagraph from '@app/domains/chapter/components/ChapterParagraph';
import ChapterPrefetcher from '@app/domains/chapter/components/ChapterPrefechter';
import ChapterTranslatorThoughts from '@app/domains/chapter/components/ChapterTranslatorThoughts';
import { checkHavingSneakPeekAccess } from '@app/domains/chapter/utils';
import NoSsr from '@app/domains/common/components/NoSsr';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import { parseChapterStatus, parseUserStatus } from '@app/domains/common/utils';
import { useAuth } from '@app/libs/auth';
import { type TagKey, type TagsStyleExceptions, useHtmlToReact } from '@app/utils/html';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Children, type ReactNode, Suspense, isValidElement, useCallback, useMemo, useState } from 'react';

type Props = {
    novel?: NovelItem;
    chapter?: ChapterItem;
};

function HtmlContent({ children, isLocked }: { children: ReactNode; isLocked: boolean | undefined }) {
    const { name: themeName } = useTheme();
    const [settings] = useChapterSettings();

    const contentStyle = useMemo(() => {
        return {
            fontSize: `${calculateFontSize(settings.fontSizeStep)}px`,
            color: settings.viewerContrast[themeName].color,
            fontFamily: settings.fontFamily,
            lineHeight: `${calculateLineHeight(settings.lineHeightStep)}px`,
        };
    }, [settings, themeName]);

    return (
        <ContentComponent
            className={clsx('relative mb-[10px] dark:text-white sm:mb-[50px]', {
                'mb-[97px] sm:mb-[50px]': Children.count(children) > 0,
            })}
            css={{
                'th,td': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                },
                'a[href^="#footnote-ref-"]': {
                    textDecoration: 'none !important',
                },
            }}
            style={contentStyle}
        >
            {children}
            {isLocked && (
                <div
                    className="absolute bottom-0 h-[100px] w-full"
                    style={{
                        background: `linear-gradient(${settings.viewerContrast[themeName].backgroundColor}1A, ${settings.viewerContrast[themeName].backgroundColor})`,
                    }}
                />
            )}
        </ContentComponent>
    );
}

export default function ChapterContent({ novel, chapter }: Props) {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const { autoUnlockEnabled } = parseUserStatus(user);
    const { isAdvancedChapter } = parseChapterStatus(chapter);

    const {
        isChapterLocked,
        isLockedBySneakPeekChapter,
        isLockedByAdvancedChapter,
        isLockedByKarmaChapter,
        isLockedByWaitToUnlockChapter,
    } = useChapterLockStatus({ novel, chapter });

    const content = useHtmlToReact(chapter?.content?.value ?? '', {
        removeStylesExceptions: ['text-align'],
        replaceFontAwesome: true,
        tagsStyleExceptions: { ...TABLE_BACKGROUND_EXCEPTION },
        transform: {
            p: {
                react(el, node) {
                    if (!isValidElement(node) || el.childNodes.length === 0) {
                        return node;
                    }

                    return (
                        <ChapterParagraph key={node.key} {...node.props}>
                            {node.props.children}
                        </ChapterParagraph>
                    );
                },
            },
        },
    });

    const translatorName = novel?.translatorName?.value ?? novel?.translator?.userName;
    const translatorThoughts = useHtmlToReact(chapter?.translatorThoughts?.value ?? '');

    const [chapterContentRef, setChapterContentRef] = useState<HTMLDivElement | null>(null);

    const handleReloadChapter = useCallback(() => {
        if (novel && chapter) {
            const invalidateSlugs = [
                chapter.slug,
                chapter.relatedChapterInfo?.previousChapter?.slug,
                chapter.relatedChapterInfo?.nextChapter?.slug,
            ].filter(Boolean);

            for (const slug of invalidateSlugs) {
                queryClient.invalidateQueries(['chapter', novel.slug, slug]);
            }
            queryClient.invalidateQueries(['chapterGroups', novel.slug]);
        }
    }, [chapter, novel, queryClient]);

    const handleUnlockChapter = useCallback(
        ({ on, type }) => {
            analyticsFactory(ChapterEvents.UnlockChapter)({
                novel,
                chapter,
                on,
                type,
                published: !isAdvancedChapter,
                autoUnlock: autoUnlockEnabled,
            });
            handleReloadChapter();
        },
        [novel, chapter, handleReloadChapter, autoUnlockEnabled, isAdvancedChapter]
    );

    return (
        <>
            <div className="content-boundary"></div>
            <div ref={setChapterContentRef} className="chapter-content">
                {(!isLockedBySneakPeekChapter ||
                    checkHavingSneakPeekAccess({ user: user ?? undefined, novelId: novel?.id })) && (
                    <HtmlContent
                        isLocked={isLockedByKarmaChapter || isLockedByWaitToUnlockChapter || isLockedByAdvancedChapter}
                    >
                        {content}
                    </HtmlContent>
                )}
                {!isChapterLocked && <ChapterPrefetcher novel={novel} chapter={chapter} />}
                {!isChapterLocked && chapter?.translatorThoughts?.value && (
                    <ChapterTranslatorThoughts
                        translatorName={translatorName}
                        translatorThoughts={translatorThoughts}
                    />
                )}
                {!!novel && !!chapter && (
                    <Suspense fallback={<LoadingFallback />}>
                        <ChapterLock
                            novel={novel}
                            chapter={chapter}
                            onReloadChapter={handleReloadChapter}
                            onUnlockChapter={handleUnlockChapter}
                        />
                    </Suspense>
                )}
                <NoSsr>
                    {!!user && !!chapter && !isChapterLocked && <ChapterBookmarkFavorite chapter={chapter} />}
                    <ChapterEditButton chapter={chapter} />
                    {user && chapter && !isChapterLocked && (
                        <ChapterBookmark
                            className="my-[20px]"
                            contentRef={chapterContentRef!}
                            novel={novel}
                            chapter={chapter}
                        />
                    )}
                </NoSsr>
                {novel && chapter && (
                    <ChapterCallToAction
                        novel={novel}
                        isChapterLocked={isChapterLocked}
                        onSelectPlan={handleReloadChapter}
                    />
                )}
            </div>
        </>
    );
}

const TABLE_BACKGROUND_EXCEPTION: TagsStyleExceptions = {
    table: {
        /**We need to add 'not-prose' otherwise translator set color style is not reflected */
        customClasses: ['not-prose'],
        doNotRemove: ['background-color'],
        backgroundAndColorException: true,
    },
    ...(() => {
        const tableTags: TagKey[] = ['thead', 'tbody', 'tr', 'th', 'td'];
        return tableTags.reduce((styleExceptions, tag) => {
            styleExceptions[tag] = { doNotRemove: ['background-color'] };
            return styleExceptions;
        }, {} as TagsStyleExceptions);
    })(),
};
